/**
 * Account Section Component
 * Account management settings including danger zone (account deletion)
 */

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
   Section,
   SectionTitle,
   Alert,
   Button,
   Card,
   FormGroup,
} from './SettingsStyles';
import styled from 'styled-components';

const DangerZone = styled(Card)`
   background: #fff3cd;
   border-color: #ffc107;
`;

const RoleText = styled.span`
   text-transform: capitalize;
`;

const SuccessAlert = styled(Alert)`
   margin-top: 1rem;
`;

const DataExportSection = styled.div`
   margin-top: 1.5rem;
`;

const SessionsSection = styled.div`
   margin-top: 1.5rem;
`;

const SubsectionTitle = styled.h3`
   margin-bottom: 1rem;
   font-size: 1.1rem;
`;

const SubsectionText = styled.p`
   color: var(--text-secondary);
   margin-bottom: 1rem;
`;

const DangerZoneSectionTitle = styled(SectionTitle)`
   color: #dc3545;
`;

const DangerZoneTitle = styled.h3`
   margin-bottom: 1rem;
   color: #856404;
`;

const DangerZoneText = styled.p`
   margin-bottom: 1.5rem;
   color: #856404;
`;

const DeleteButtonGroup = styled.div`
   display: flex;
   gap: 1rem;
`;

const AccountSection = () => {
   const { user, logout } = useAuth();
   const navigate = useNavigate();
   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
   const [confirmText, setConfirmText] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState('');
   const [successMessage, setSuccessMessage] = useState('');

   const handleExportData = async () => {
      try {
         setLoading(true);
         setError('');
         
         const response = await fetch('/api/auth/export-data', {
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
         });

         if (!response.ok) {
            throw new Error('Failed to export data');
         }

         const result = await response.json();
         
         // Create a blob and download the JSON file
         const dataStr = JSON.stringify(result.data, null, 2);
         const dataBlob = new Blob([dataStr], { type: 'application/json' });
         const url = URL.createObjectURL(dataBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `user-data-export-${new Date().toISOString().split('T')[0]}.json`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);
         
         setSuccessMessage('Your data has been exported successfully!');
         setTimeout(() => setSuccessMessage(''), 5000);
      } catch (err) {
         setError('Failed to export data. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   const handleLogoutAll = async () => {
      try {
         setLoading(true);
         // In a real app, you'd call an API to invalidate all sessions
         await logout();
         navigate('/login');
      } catch (err) {
         setError('Failed to log out. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   const handleDeleteAccount = async () => {
      if (confirmText !== 'DELETE') {
         setError('Please type DELETE to confirm');
         return;
      }

      try {
         setLoading(true);
         // In a real app, you'd call an API to delete the account
         const response = await fetch(`/api/users/${user._id}`, {
            method: 'DELETE',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
         });

         if (!response.ok) {
            throw new Error('Failed to delete account');
         }

         await logout();
         navigate('/');
      } catch (err) {
         setError(err.message || 'Failed to delete account. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   return (
      <>
         <Section>
            <SectionTitle>Account Management</SectionTitle>
            
            <Alert $type="info">
               <strong>ℹ️ Account Type:</strong>{' '}
               <RoleText>{user?.role || 'User'}</RoleText>
            </Alert>

            {successMessage && (
               <SuccessAlert $type="success">
                  {successMessage}
               </SuccessAlert>
            )}

            <DataExportSection>
               <SubsectionTitle>Data Export</SubsectionTitle>
               <SubsectionText>
                  Download a copy of all your personal data, account information, and security settings in JSON format.
               </SubsectionText>
               <Button onClick={handleExportData} disabled={loading}>
                  {loading ? 'Exporting...' : '📥 Export My Data'}
               </Button>
            </DataExportSection>

            <SessionsSection>
               <SubsectionTitle>Sessions</SubsectionTitle>
               <SubsectionText>
                  Manage your active sessions and sign out from all devices.
               </SubsectionText>
               <Button onClick={handleLogoutAll} disabled={loading}>
                  {loading ? 'Logging out...' : 'Log Out All Sessions'}
               </Button>
            </SessionsSection>
         </Section>

         <Section>
            <DangerZoneSectionTitle>⚠️ Danger Zone</DangerZoneSectionTitle>
            
            <DangerZone>
               <DangerZoneTitle>
                  Delete Account
               </DangerZoneTitle>
               <DangerZoneText>
                  Once you delete your account, there is no going back. This action cannot be
                  undone. All your data, including courses and progress, will be permanently
                  deleted.
               </DangerZoneText>

               {!showDeleteConfirm ? (
                  <Button
                     $variant="danger"
                     onClick={() => setShowDeleteConfirm(true)}
                     disabled={loading}
                  >
                     Delete Account
                  </Button>
               ) : (
                  <>
                     <Alert $type="error">
                        <strong>⚠️ Warning:</strong> This action is permanent and cannot be
                        undone!
                     </Alert>

                     <FormGroup>
                        <label>
                           Type <strong>DELETE</strong> to confirm:
                        </label>
                        <input
                           type="text"
                           value={confirmText}
                           onChange={e => setConfirmText(e.target.value)}
                           placeholder="Type DELETE"
                           disabled={loading}
                        />
                     </FormGroup>

                     {error && <Alert $type="error">{error}</Alert>}

                     <DeleteButtonGroup>
                        <Button
                           $variant="danger"
                           onClick={handleDeleteAccount}
                           disabled={loading || confirmText !== 'DELETE'}
                        >
                           {loading ? 'Deleting...' : 'Confirm Delete Account'}
                        </Button>
                        <Button
                           $variant="secondary"
                           onClick={() => {
                              setShowDeleteConfirm(false);
                              setConfirmText('');
                              setError('');
                           }}
                           disabled={loading}
                        >
                           Cancel
                        </Button>
                     </DeleteButtonGroup>
                  </>
               )}
            </DangerZone>
         </Section>
      </>
   );
};

export default AccountSection;
